import { useEventNameMatch, useEventUserRole } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useTranslation } from '@shared/core';
import { EventWebsiteGuestDocument, GetEventSessionDocument, useGetEventSessionQuery, useLogoutEventSessionMutation } from '@graphql/generated';
import { performFullPageRedirect } from '@shared/utils/navigation';
import { useCallback } from 'react';
import { GraphQLError } from 'graphql';

export const useCtaButtonsController = (props: { onRSVPButtonClicked?: () => void; onNotYouButtonClicked?: () => void; onNotYouErrorRequest?: (err: GraphQLError) => void }) => {
  const onRSVPButtonClicked = props.onRSVPButtonClicked;
  const { eventName } = useEventNameMatch();

  const [logoutEvent] = useLogoutEventSessionMutation();

  // Marking this query as `ssr: false` because any RSVP data query is proxied through app-server-api -- which takes long to resolve.
  const { data: sessionData } = useGetEventSessionQuery({ variables: { eventHandle: eventName || '' }, batchMode: 'fast', ssr: false });

  const { isAdmin } = useEventUserRole();
  const guestName = sessionData?.eventByName?.activeSession.person?.firstName;
  const hasUnlocked = sessionData?.eventByName?.activeSession.hasUnlocked;
  const hasIdentity = sessionData?.eventByName?.activeSession.hasIdentity;
  const rsvpLabel = sessionData?.eventByName?.activeSession.eventCTA.label;
  const rsvpIsEnabled = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsEnabled;
  const rsvpIsCompleted = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsCompleted;
  const isRsvpRequiresPassword = sessionData?.eventByName?.activeSession?.eventCTA?.requiresPassword;
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus;
  const scheduleHasInviteOnlyItems = sessionData?.eventByName?.activeSession?.eventCTA?.scheduleHasInviteOnlyItems;
  const isRsvpUnlocked = !isRsvpRequiresPassword || hasUnlocked;
  const { t2, t } = useTranslation('layoutEngine');
  const { buttonNotYouText } = t2('components', 'eventCta');
  const personalizedText = t('components', 'eventCta', 'personalizedText')({ guestName });
  const rsvpButtonTextDefault = t2('components', 'eventCta', 'rsvpButtonText');
  const rsvpButtonText = rsvpAttendanceStatus === 'noAttendanceQuestion' && rsvpLabel === 'RSVP Here' ? rsvpButtonTextDefault : rsvpLabel;
  const isRsvpDisplayed = rsvpIsEnabled && !rsvpIsCompleted && isRsvpUnlocked;

  const rsvpHandle = useCallback(() => {
    onRSVPButtonClicked?.();
    performFullPageRedirect(`/${eventName}/rsvp`);
  }, [onRSVPButtonClicked, eventName]);

  const logoutHandle = async () => {
    await logoutEvent({ refetchQueries: () => [GetEventSessionDocument, EventWebsiteGuestDocument] })
      .then(() => props.onNotYouButtonClicked && props.onNotYouButtonClicked())
      .catch(err => props.onNotYouErrorRequest && props.onNotYouErrorRequest(err));
  };

  return {
    isAdmin,
    guestName,
    personalizedText,
    buttonNotYouText,
    rsvpButtonText,
    isRsvpDisplayed,
    rsvpHandle,
    scheduleHasInviteOnlyItems,
    hasUnlocked,
    hasIdentity,
    logoutHandle
  };
};
